// ===========================================
// PROCUREMENT FLOW STRIP
// ===========================================
// The purchasing cycle is one flow — count stock, cost recipes, watch
// margins, order, receive, reconcile — but it lives across six pages.
// This strip keeps the sequence visible and one tap away from any of them.

import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { restaurantApi } from '../services/api';
import clsx from 'clsx';
import { Package, BookOpen, TrendingUp, ShoppingCart, Truck, FileText, Building2, ChevronRight } from 'lucide-react';

const STEPS = [
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/recipes', label: 'Recipes', icon: BookOpen },
  { path: '/food-cost', label: 'Food Cost', icon: TrendingUp },
  { path: '/ordering', label: 'Ordering', icon: ShoppingCart },
  { path: '/receiving', label: 'Receiving', icon: Truck },
  { path: '/invoices', label: 'Invoices', icon: FileText },
];

export default function ProcurementNav() {
  const { pathname } = useLocation();

  // Wholesale is opt-in per restaurant (settings.modules.wholesale) — most
  // restaurants never need accounts-receivable and shouldn't see it. Read the
  // flag from the live restaurant record (auth/me's cached copy omits settings).
  const { data: wholesaleOn } = useQuery({
    queryKey: ['module-wholesale'],
    queryFn: async () => {
      const res = await restaurantApi.getCurrent();
      const settings = (res.data?.data || res.data)?.settings || {};
      return !!settings?.modules?.wholesale;
    },
    staleTime: 5 * 60 * 1000,
  });
  const steps = wholesaleOn
    ? [...STEPS, { path: '/wholesale', label: 'Wholesale', icon: Building2 }]
    : STEPS;

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1 mb-4">
      {steps.map((step, i) => {
        const active = pathname.startsWith(step.path);
        return (
          <div key={step.path} className="flex items-center shrink-0">
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300 mx-0.5" />}
            <Link
              to={step.path}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                active
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              <step.icon className="w-3.5 h-3.5" />
              {step.label}
            </Link>
          </div>
        );
      })}
    </div>
  );
}
